import type { RecipeRevision } from '@recipes/contracts';
import { diffRecipes } from '@recipes/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, EmptyState, Skeleton } from '@recipes/ui';
import { format } from 'date-fns';
import { de as deLocale, enUS } from 'date-fns/locale';
import { History, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { ConfirmDialog } from '@/components/forms/confirm-dialog';
import { toast } from '@/components/feedback/toaster';
import { PageHeader } from '@/components/layout/page-header';
import { useRecipe, useRevisions, useSetRecipeCache } from '@/features/recipes/queries';
import { recipeToWriteInput } from '@/features/recipes/recipe-to-input';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { currentLocale } from '@/lib/i18n';
import { queryKeys } from '@/lib/query-keys';

function RevisionDetail({ recipeId, revision }: { recipeId: string; revision: RecipeRevision }) {
  const { t } = useTranslation();
  const current = useRecipe(recipeId);
  const detail = useQuery({
    queryKey: [...queryKeys.recipes.revisions(recipeId), revision.revision],
    queryFn: () => api.recipes.revision(recipeId, revision.revision),
  });
  if (detail.isPending) return <Skeleton className="h-24" />;
  if (detail.isError || !detail.data)
    return <p className="text-sm text-destructive">{t('recipes.history.loadError')}</p>;
  const snapshot = detail.data.snapshot;
  const diff = current.data ? diffRecipes(snapshot, recipeToWriteInput(current.data.recipe)) : null;
  const rows = diff
    ? [
        ...diff.changes.map((c) => c.label),
        ...diff.ingredientsAdded.map((i) => `+ ${i}`),
        ...diff.ingredientsRemoved.map((i) => `− ${i}`),
        ...diff.ingredientsChanged.map((i) => `${i.before} → ${i.after}`),
        ...diff.stepsAdded.map((s) => `+ ${s.slice(0, 80)}`),
        ...diff.stepsRemoved.map((s) => `− ${s.slice(0, 80)}`),
        ...diff.stepsChanged.map((s) => `~ ${s.after.slice(0, 80)}`),
      ]
    : [];
  return (
    <div className="space-y-3 rounded-md bg-muted/40 p-3 text-sm" data-testid="revision-detail">
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div>
          <dt className="text-xs text-muted-foreground">{t('editor.fields.title')}</dt>
          <dd className="font-medium">{snapshot.title}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{t('common.servings')}</dt>
          <dd className="font-medium">{snapshot.servings}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{t('recipes.detail.ingredients')}</dt>
          <dd className="font-medium">{revision.summary.ingredientCount}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{t('recipes.detail.steps')}</dt>
          <dd className="font-medium">{revision.summary.stepCount}</dd>
        </div>
      </dl>
      <div>
        <h4 className="mb-1 font-medium">{t('recipes.history.diffToCurrent')}</h4>
        {rows.length === 0 ? (
          <p className="text-muted-foreground">{t('recipes.history.identical')}</p>
        ) : (
          <ul className="list-disc space-y-0.5 pl-5">
            {rows.map((row, i) => (
              <li key={i}>{row}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function RecipeHistoryPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setCache = useSetRecipeCache();
  const dateLocale = currentLocale() === 'de' ? deLocale : enUS;
  const recipe = useRecipe(id);
  const revisions = useRevisions(id);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<RecipeRevision | null>(null);

  const restore = useMutation({
    mutationFn: (revision: number) => api.recipes.restoreRevision(id!, revision),
    onSuccess: (restored) => {
      setCache(restored);
      void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.revisions(id!) });
      toast.success(t('recipes.history.restored'));
      setRestoreTarget(null);
      navigate(`/recipes/${id}`);
    },
    onError: (e) => toastError(e),
  });

  const title = recipe.data?.recipe.title ?? t('recipes.history.title');
  const items = revisions.data ? [...revisions.data].sort((a, b) => b.revision - a.revision) : [];

  return (
    <div className="mx-auto max-w-3xl p-4 md:p-6" data-testid="history-page">
      <PageHeader
        eyebrow={
          <Link to={`/recipes/${id}`} className="underline-offset-4 hover:underline">
            ← {title}
          </Link>
        }
        title={t('recipes.history.title')}
        description={t('recipes.history.description')}
      />
      {revisions.isPending ? (
        <div className="space-y-2" aria-busy="true">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      ) : revisions.isError ? (
        <EmptyState title={t('app.unexpectedError')} description={t('recipes.history.loadError')} />
      ) : items.length === 0 ? (
        <EmptyState icon={<History />} title={t('recipes.history.empty')} />
      ) : (
        <ol className="divide-y rounded-lg border" data-testid="revision-list">
          {items.map((rev) => {
            const isCurrent = recipe.data && rev.revision === recipe.data.recipe.version;
            return (
              <li key={rev.id} className="p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    {t('recipes.history.revision', { n: rev.revision })}
                  </span>
                  {isCurrent && <Badge variant="success">{t('recipes.history.current')}</Badge>}
                  <time dateTime={rev.createdAt} className="text-sm text-muted-foreground">
                    {format(new Date(rev.createdAt), 'PPp', { locale: dateLocale })}
                  </time>
                  {rev.actor && (
                    <span className="text-sm text-muted-foreground">· {rev.actor.displayName}</span>
                  )}
                  {rev.reason && (
                    <span className="text-sm italic text-muted-foreground">· {rev.reason}</span>
                  )}
                  <span className="ml-auto flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-expanded={expanded === rev.revision}
                      onClick={() => setExpanded(expanded === rev.revision ? null : rev.revision)}
                    >
                      {expanded === rev.revision
                        ? t('recipes.history.hide')
                        : t('recipes.history.view')}
                    </Button>
                    {!isCurrent && recipe.data?.recipe.permissions.canEdit && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRestoreTarget(rev)}
                        data-testid="restore-revision"
                      >
                        <RotateCcw aria-hidden /> {t('recipes.history.restore')}
                      </Button>
                    )}
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {rev.summary.title} ·{' '}
                  {t('recipes.history.summary', {
                    ingredients: rev.summary.ingredientCount,
                    steps: rev.summary.stepCount,
                  })}
                </p>
                {expanded === rev.revision && (
                  <div className="mt-2">
                    <RevisionDetail recipeId={id!} revision={rev} />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
      <ConfirmDialog
        open={restoreTarget !== null}
        onOpenChange={(open) => !open && setRestoreTarget(null)}
        title={t('recipes.history.restoreTitle', { n: restoreTarget?.revision ?? 0 })}
        description={t('recipes.history.restoreDescription')}
        confirmLabel={t('recipes.history.restore')}
        loading={restore.isPending}
        onConfirm={() => {
          if (restoreTarget) restore.mutate(restoreTarget.revision);
        }}
      />
    </div>
  );
}
